import { PINNED_TIMEZONES, TIMEZONE_ALIASES, TIMEZONES } from '@Constants';

interface TimezoneOption {
  label: string;
  offset: number;
  value: string;
}

const UNKNOWN_OFFSET = Number.POSITIVE_INFINITY;

const LISTED_TIMEZONES = new Set(TIMEZONES.map(({ value }) => value));

// NOTE :: `TIMEZONES` carries i18n keys because @FormModule renders option labels as-is, so the
// keys have to be resolved before they reach the dropdown. The caller passes its own `t` from
// `useI18n()` — the helper stays pure and store-free. Pass `currentTimezone` whenever the record
// already holds one, so a value the curated list dropped stays selectable. Every label is prefixed
// with its current UTC offset and the list is sorted by that offset, the way calendar apps do it,
// so the order is visible to the person picking. The `PINNED_TIMEZONES` come first, in their own
// order, because most shelters are in the US and Canada.
function getTimezoneOptions(translate: (key: string) => string, currentTimezone?: string) {
  const options = TIMEZONES.map(({ label, value }) => getTimezoneOption(translate(label), value));

  return withSavedTimezone(options, currentTimezone);
}

// NOTE :: Shelters saved before the list was trimmed sit on zones that are no longer offered
// (`America/Detroit`, …). Dropping one would render an empty selector and let the next save
// overwrite a correct timezone with a blank one, so it is appended as its own option. Takes options
// already resolved, so a caller holding a built list does not need `t` again. Always re-sorts, so
// the appended zone lands next to its neighbours instead of at the bottom (never in the pinned group).
// It **rebases** rather than appends: a zone a previous call added is dropped first, so a caller that
// rewrites the options on every address pick (`applyShelterDefaultsToSchema`) cannot stack each
// earlier guess into the list.
function withSavedTimezone(options: TimezoneOption[], currentTimezone?: string) {
  const curated = options.filter(({ value }) => LISTED_TIMEZONES.has(value));
  const isListed = !currentTimezone || curated.some(({ value }) => value === currentTimezone);
  const allOptions = isListed ? curated : [...curated, getSavedTimezoneOption(currentTimezone as string, curated)];

  return sortWithPinned(allOptions);
}

// HELPERS

// NOTE :: The option for a saved zone the curated list does not carry. A legacy IANA *link* name
// (`US/Eastern`, `Brazil/East`) points at a zone that is already on the list and keeps the identical
// clock, so it borrows that row's label — offset prefix included — and carries its own id beside it,
// which is what keeps the two rows apart. Its id is never rewritten. Anything else is built the
// normal way and falls back to the city at the end of the id.
function getSavedTimezoneOption(timezone: string, options: TimezoneOption[]): TimezoneOption {
  const aliasedTimezone = TIMEZONE_ALIASES[timezone];
  const aliasedOption = aliasedTimezone && options.find(({ value }) => value === aliasedTimezone);

  if (aliasedOption) return { ...aliasedOption, label: `${aliasedOption.label} — ${timezone}`, value: timezone };

  return getTimezoneOption(getTimezoneLabel(timezone), timezone);
}

function getTimezoneOption(label: string, value: string): TimezoneOption {
  const offset = getTimezoneOffset(value);
  const prefix = offset === UNKNOWN_OFFSET ? '' : `(${getOffsetLabel(offset)}) `;

  return { label: `${prefix}${label}`, offset, value };
}

// NOTE :: Reads the offset from the browser's own zone data rather than moment-timezone, whose
// bundled data lags behind (British Columbia and Alberta changed their rules in 2026). Today's
// offset is used, so a zone on daylight time shows its summer value, as calendar apps do. An id the
// browser does not know (a corrupt saved value) sorts last, without a prefix.
function getTimezoneOffset(timezone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longOffset' }).formatToParts(
      new Date()
    );
    const offsetName = parts.find(({ type }) => type === 'timeZoneName')?.value ?? '';
    const match = offsetName.match(/([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;

    const [, sign, hours, minutes] = match;
    const totalMinutes = Number(hours) * 60 + Number(minutes);

    return sign === '-' ? -totalMinutes : totalMinutes;
  } catch {
    return UNKNOWN_OFFSET;
  }
}

function getOffsetLabel(offset: number) {
  const sign = offset < 0 ? '-' : '+';
  const absolute = Math.abs(offset);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  const minutes = String(absolute % 60).padStart(2, '0');

  return `UTC${sign}${hours}:${minutes}`;
}

// NOTE :: Fallback label for a zone outside the curated list, so it has no key of its own. Reads
// the city off the end of the IANA id so `America/Argentina/Buenos_Aires` becomes "Buenos Aires"
// rather than repeating the underscored id back at the user. Place names stay untranslated.
function getTimezoneLabel(timezone: string) {
  const city = timezone.split('/').pop() || timezone;

  return city.replace(/_/g, ' ');
}

function sortByOffset(options: TimezoneOption[]) {
  return [...options].sort((first, second) => first.offset - second.offset || first.label.localeCompare(second.label));
}

// NOTE :: Pinned zones appear once, at the top, so a selected value never highlights two rows.
function sortWithPinned(options: TimezoneOption[]) {
  const pinned = PINNED_TIMEZONES.map((timezone) => options.find(({ value }) => value === timezone)).filter(
    (option): option is TimezoneOption => !!option
  );
  const rest = options.filter(({ value }) => !PINNED_TIMEZONES.includes(value));

  return [...pinned, ...sortByOffset(rest)];
}

export { getTimezoneOptions, withSavedTimezone };
